import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { promoCodes, shippingOptions, store, TODAY } from '../data/store';
import {
  digitsOnly,
  generateOrderNumber,
  validateStep,
  type CheckoutForm,
  type CheckoutStep,
  type FormErrors,
} from '../utils/checkout';
import { getShippingCost } from '../utils/shipping';
import type { Order } from '../types/cart';

export const stepOrder: CheckoutStep[] = ['contact', 'delivery', 'payment'];

const initialForm: CheckoutForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  shippingId: 'pickup',
  address: '',
  postalCode: '',
  city: '',
  giftWrap: false,
  giftMessage: '',
  cardName: '',
  cardNumber: '',
  cardExpiry: '',
  cardCvc: '',
  acceptTerms: false,
};

export type PaymentStatus = 'idle' | 'processing' | 'declined';

export function useCheckout() {
  const { lines, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState<CheckoutStep>('contact');
  const [completed, setCompleted] = useState<CheckoutStep[]>([]);
  const [form, setForm] = useState<CheckoutForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [promo, setPromo] = useState<{ code: string; rate: number } | null>(null);
  const [status, setStatus] = useState<PaymentStatus>('idle');

  const shipping = shippingOptions.find((o) => o.id === form.shippingId) ?? shippingOptions[0];
  const shippingCost = getShippingCost(shipping, subtotal);
  const discount = promo ? Math.round(subtotal * promo.rate * 100) / 100 : 0;
  const total = Math.max(0, subtotal - discount + shippingCost);

  const update = useCallback(<K extends keyof CheckoutForm>(field: K, value: CheckoutForm[K]) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
    setStatus((s) => (s === 'declined' ? 'idle' : s));
  }, []);

  const submitStep = (current: CheckoutStep): boolean => {
    const e = validateStep(current, form, TODAY);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      window.setTimeout(() => {
        const first = document.querySelector<HTMLElement>('[aria-invalid="true"]');
        first?.focus();
      }, 20);
      return false;
    }
    setCompleted((c) => (c.includes(current) ? c : [...c, current]));
    const idx = stepOrder.indexOf(current);
    if (idx < stepOrder.length - 1) setStep(stepOrder[idx + 1]);
    return true;
  };

  const editStep = (s: CheckoutStep) => {
    setErrors({});
    setStep(s);
  };

  const applyPromo = (code: string): boolean => {
    const key = code.trim().toUpperCase();
    const rate = promoCodes[key];
    if (!rate) return false;
    setPromo({ code: key, rate });
    return true;
  };

  const removePromo = () => setPromo(null);

  const placeOrder = () => {
    const e = validateStep('payment', form, TODAY);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStatus('processing');
    window.setTimeout(() => {
      if (digitsOnly(form.cardNumber) === '4000000000000002') {
        setStatus('declined');
        return;
      }
      const order: Order = {
        number: generateOrderNumber(),
        email: form.email,
        firstName: form.firstName,
        lines,
        subtotal,
        discount,
        shippingCost,
        total,
        shippingId: shipping.id,
        shippingLabel: shipping.label,
        address:
          shipping.id === 'pickup'
            ? `${store.address}, ${store.city}`
            : `${form.address}, ${form.postalCode} ${form.city}`,
        giftWrap: form.giftWrap,
        createdAt: TODAY.toISOString(),
      };
      navigate('/commande/confirmation', { state: { order }, replace: true });
      clearCart();
    }, 1600);
  };

  return {
    lines,
    subtotal,
    step,
    completed,
    form,
    errors,
    promo,
    status,
    shipping,
    shippingCost,
    discount,
    total,
    update,
    submitStep,
    editStep,
    applyPromo,
    removePromo,
    placeOrder,
  };
}

export type CheckoutState = ReturnType<typeof useCheckout>;
